import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import { Circles } from 'react-loader-spinner';
import 'react-quill-new/dist/quill.snow.css';
import './AdminEditReport.css';
import privateAxios from '../../utils/axios/privateAxios';
import publicAxios from '../../utils/axios/publicAxios';
import Notification from '../../components/Notification/Notification';
import { formats, modules, reportCategories } from '../../assets/exports';

const AdminEditReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [year, setYear] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [originalPdfUrl, setOriginalPdfUrl] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [display, setDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const res = await privateAxios.get(`/private/admin-report/${id}`);
        const { title, year, category, content, pdfUrl, display } = res.data.report;

        setTitle(title || '');
        setYear(year || '');
        setCategory(category || '');
        setContent(content || '');
        setPdfUrl(pdfUrl || '');
        setOriginalPdfUrl(pdfUrl || '');
        setPdfName(pdfUrl ? pdfUrl.split('/').pop() : '');
        setDisplay(display ?? false);
      } catch (err) {
        console.error('Failed to load report:', err);
        showNotification('error', 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfFile(file);
    setPdfName(file.name);
    setPdfUrl(URL.createObjectURL(file));
  };

  const uploadPdfToS3 = async () => {
    if (!pdfFile) return originalPdfUrl;

    try {
      const res = await privateAxios.get('/private/generate-pdf-upload-url', {
        params: { filename: pdfFile.name, filetype: pdfFile.type },
      });

      const { url } = res.data;
      await publicAxios.put(url, pdfFile);
      return url.split('?')[0];
    } catch (error) {
      console.error('PDF upload failed:', error);
      throw new Error('Failed to upload PDF.');
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);

    try {
      let updatedPdfUrl = originalPdfUrl;

      if (pdfFile) {
        updatedPdfUrl = await uploadPdfToS3();
      }

      await privateAxios.patch(`/private/admin-edit-report/${id}`, {
        title,
        year,
        category,
        content,
        pdfUrl: updatedPdfUrl,
        display,
      });

      showNotification('success', 'Report updated successfully!');
      setTimeout(() => navigate('/admin-reports'), 3000);
    } catch (error) {
      console.error('Failed to update report:', error);
      showNotification('error', 'Failed to update report');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="admin-edit-report">
      <Notification
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification({ type: '', message: '' })}
      />

      <div className='admin-edit-report-title'>
        <h2>Edit Report</h2>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
          <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h2>Loading Team Members...</h2>
        </div>
      ) : (
        <div className="edit-form">
          {/* Title */}
          <label>Title:</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          {/* Year */}
          <label>Year:</label>
          <input
            type="text"
            className="admin-edit-report-year-input"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="Enter year (e.g., 2025 - 2026)"
          />

          {/* Category */}
          <label>Category:</label>
          <select
            className="admin-edit-report-select-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Select category</option>
            {reportCategories.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>

          {/* PDF */}
          <label>Change PDF (optional):</label>
          <input type="file" accept="application/pdf" onChange={handlePdfChange} />
          {pdfName && (
            <div className="admin-edit-report-pdf-preview">
              <p>Selected file: {pdfName}</p>
            </div>
          )}

          {/* Content */}
          <label>Content:</label>
          <ReactQuill
            value={content}
            onChange={setContent}
            theme="snow"
            className="quill-editor"
            modules={modules}
            formats={formats}
          />

          {/* Display */}
          <div className="checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={display}
                onChange={(e) => setDisplay(e.target.checked)}
                className="admin-edit-report-checkbox"
              />
              Display report publicly
            </label>
          </div>

          {/* Actions */}
          <div className="edit-actions">
            <button onClick={handleUpdate} disabled={updating}>
              {updating ? 'Updating...' : 'Save Changes'}
            </button>
            <button onClick={() => navigate('/admin-reports')}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditReport;